import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar, Box, CircularProgress, Divider, Fade,
  IconButton, Paper, Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import invitationApi from './invitationApi';

const POLL_INTERVAL_MS = 5000;

function avatarLetters(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function MessageBubble({ msg, isMine }) {
  const time = msg.sent_at
    ? new Date(msg.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';
  return (
    <Stack
      direction={isMine ? 'row-reverse' : 'row'}
      spacing={1}
      alignItems="flex-end"
      sx={{ maxWidth: '80%', alignSelf: isMine ? 'flex-end' : 'flex-start' }}
    >
      {!isMine && (
        <Avatar src={msg.sender_avatar || undefined} sx={{ width: 28, height: 28, fontSize: 11, flexShrink: 0 }}>
          {avatarLetters(msg.sender_name)}
        </Avatar>
      )}
      <Box>
        {!isMine && (
          <Typography variant="caption" color="text.secondary" display="block" ml={0.5} mb={0.25}>
            {msg.sender_name}
          </Typography>
        )}
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
            bgcolor: isMine ? 'primary.main' : 'action.hover',
            color: isMine ? 'primary.contrastText' : 'text.primary',
            wordBreak: 'break-word',
          }}
        >
          <Typography variant="body2">{msg.message_text}</Typography>
        </Box>
        <Typography variant="caption" color="text.disabled" display="block"
          sx={{ mt: 0.25, textAlign: isMine ? 'right' : 'left', ml: isMine ? 0 : 0.5 }}>
          {time}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function InvitationChat({ invitation, onClose, token, viewerRole }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const invitationId = invitation?.invitation_id;
  const isTokenMode = Boolean(token);
  // Which side of the conversation the current viewer is on. Messages from
  // that side render on the right — derived server-side via `from_recipient`
  // so it stays correct under organisation-context identities.
  const role = viewerRole || (isTokenMode ? 'recipient' : 'organiser');

  const fetchMessages = useCallback(async () => {
    if (!invitationId) return;
    try {
      const data = isTokenMode
        ? await invitationApi.getMessagesPublic(invitationId, token)
        : await invitationApi.getMessages(invitationId);
      setMessages(data.messages || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [invitationId, isTokenMode, token]);

  // Initial load + polling while popup is open
  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchMessages]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      if (isTokenMode) {
        await invitationApi.sendMessagePublic(invitationId, trimmed, token);
      } else {
        await invitationApi.sendMessage(invitationId, trimmed);
      }
      setText('');
      fetchMessages();
    } catch {
      // silent
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const title = invitation?.event_name
    ? `Chat — ${invitation.event_name}`
    : 'Chat';

  return (
    <Fade in>
      <Paper
        elevation={12}
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 24,
          width: 340,
          maxWidth: 'calc(100vw - 32px)',
          height: 460,
          maxHeight: 'calc(100vh - 100px)',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1400,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1.25}
          sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', flexShrink: 0 }}
        >
          <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1, mr: 1 }}>
            {title}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: 'primary.contrastText' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Divider />

        {/* Messages area */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : messages.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" mt={4}>
              No messages yet. Say hello!
            </Typography>
          ) : (
            <Stack spacing={1}>
              {messages.map(msg => (
                <MessageBubble
                  key={msg.message_id}
                  msg={msg}
                  isMine={role === 'recipient' ? msg.from_recipient === true : !msg.from_recipient}
                />
              ))}
              <div ref={bottomRef} />
            </Stack>
          )}
        </Box>

        <Divider />

        {/* Input area */}
        <Stack direction="row" spacing={0.5} alignItems="flex-end" px={1} py={0.75} sx={{ flexShrink: 0 }}>
          <TextField
            size="small"
            multiline
            maxRows={3}
            placeholder="Type a message…"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            fullWidth
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!text.trim() || sending}
            sx={{ mb: 0.25 }}
          >
            {sending ? <CircularProgress size={18} /> : <SendIcon />}
          </IconButton>
        </Stack>
      </Paper>
    </Fade>
  );
}
